import { z } from 'zod';
import { RoleSchema } from './common.schema.js';

/**
 * SIGN-IN AND SECURITY POLICY — password rules, session lifetime, the lockout
 * counter, the kiosk PIN length, the device-approval gate and whether the
 * company offers two-factor at all.
 *
 * It lives in its own module for the reason `erp.schema.ts` does: every member
 * of this object carries a paragraph explaining what breaks when it is set
 * wrong (a session timeout that silently overrode the refresh TTL, a device
 * gate that queues the whole company on the day it is switched on), and
 * `settings.schema.ts` was long past its budget carrying them. `settings.schema.ts`
 * re-exports it, so no import site changes.
 */
export const SecuritySettingsSchema = z.object({
  passwordMinLength: z.number().int().min(6).max(64).default(8),
  passwordRequireUppercase: z.boolean().default(true),
  passwordRequireNumber: z.boolean().default(true),
  passwordRequireSpecial: z.boolean().default(false),
  /**
   * How long a sign-in survives without activity, in minutes.
   *
   * This is the SHORTER of the two limits on a session: `effectiveRefreshTtlMs`
   * takes `min(JWT_REFRESH_TTL, this)`, so it silently overrides the env value.
   * With the old default of 60 the 7-day refresh token was decorative — every
   * session actually died an hour after its last request.
   *
   * That is a bank's idle timeout on an internal attendance tool, and it was a
   * schema default nobody chose. It slides while a tab is polling, so it looked
   * fine at a desk; a backgrounded tab, a closed laptop or a phone in a pocket
   * stops polling (browsers throttle background timers), and the worker comes
   * back to a login screen. A site supervisor was signed out over lunch.
   *
   * 1440 — one working day — means signing in once each morning, which is what
   * people expect from a tool they use all day. The ceiling is a week so a
   * tenant can align it with `JWT_REFRESH_TTL` without a code change; raising
   * it beyond that would be pointless, since the env TTL caps it anyway.
   */
  sessionTimeoutMinutes: z.number().int().min(5).max(10080).default(1440),
  maxFailedLoginAttempts: z.number().int().min(3).max(20).default(5),
  lockoutDurationMinutes: z.number().int().min(1).max(720).default(30),
  pinLength: z.number().int().min(4).max(8).default(4),

  /**
   * Whether anyone in this company may use an authenticator app as a second
   * factor at sign-in.
   *
   * `optional` (the default, and what every existing install has) is exactly
   * today's behaviour: two-factor is a personal choice, enrolled from the
   * profile page, and login asks for a code from whoever turned it on.
   *
   * `off` withdraws the feature for the whole company: nobody can start an
   * enrolment, and login stops asking for a code even from people who already
   * enrolled. It does NOT erase their secret — `User.totpSecret` and
   * `totpEnabled` are left exactly as they are, so switching back to
   * `optional` restores the factor those people already carry rather than
   * silently unenrolling them. That is the whole reason this is a company
   * setting and not a bulk write over the user table: turning a security
   * control off must be reversible, and must not destroy anything.
   */
  twoFactorMode: z.enum(['optional', 'off']).default('optional'),

  /**
   * Device approval gate for WEB sign-in.
   *
   * `off` (the default, and the only value an existing install has) is exactly
   * today's behaviour: the browser id is recorded on the session and nothing
   * reads it. `gate_login` makes an unknown browser unusable until an admin
   * approves it — the password and the second factor are still checked first,
   * so only a fully authenticated person ever creates a queue row.
   *
   * It defaults OFF and must STAY off until the rollout backfill has run
   * (packages/api/src/scripts/backfill-user-devices.ts). Every browser signed
   * in today is unknown to the UserDevice table; switching this on without the
   * backfill queues the entire company at once, including whoever would have to
   * approve it.
   */
  deviceApprovalMode: z.enum(['off', 'gate_login']).default('off'),

  /**
   * Roles the gate never applies to. ADMIN by default and deliberately so: the
   * approval queue is an ADMIN screen, so an admin locked out by their own
   * pending device cannot reach the page that would release them. That is not a
   * support call, it is a DBA call. Emptying this list is a supported choice —
   * it just means keeping a second, already-approved admin browser.
   */
  deviceApprovalExemptRoles: z.array(RoleSchema).default(['ADMIN']),
});
